import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import type { LoadedModel, Model } from '../models';
import { PromiseCompletion } from '../classes/PromiseCompletion';
import apiService from '../services/apiService';
import { defaulComparator, stringSearchService } from '../services/StringSearchService';
import { MemoryBar } from './MemoryBar';

type ModelSelectorProps = {
  model?: Model | null;
  onModelChange: (model: Model | null) => void;
}

@observer
class ModelSelector extends Component<ModelSelectorProps> {
  @observable private _isDropdownOpen: boolean = false;
  @observable private _models: Model[] = [];
  @observable private _activeModels: LoadedModel[] = [];
  @observable private _searchFilter: string = '';
  @observable private _categoryFilters: Set<string> = new Set();
  private _refreshInterval: number | null = null;
  private _loader: PromiseCompletion = new PromiseCompletion();
  private _searchInputRef: React.RefObject<HTMLInputElement | null> = React.createRef();

  constructor(props: ModelSelectorProps) {
    super(props);
    makeObservable(this);
  }

  componentDidMount() {
    void this._loadModels();
    document.addEventListener('click', this._onDocumentClick);
    this._refreshInterval = window.setInterval(() => {
      void this.loadActiveModels();
    }, 1000 * 60 * 1); // Refresh every 1 minutes
    void this.loadActiveModels();

    document.addEventListener('modelsReloaded', this._onModelsReloaded);
    document.addEventListener('visibilitychange', this._onDocumentVisibilitychange);
  }

  componentWillUnmount(): void {
    document.removeEventListener('click', this._onDocumentClick);
    if (this._refreshInterval) {
      clearInterval(this._refreshInterval);
      this._refreshInterval = null;
    }
    document.removeEventListener('modelsReloaded', this._onModelsReloaded);
    document.removeEventListener('visibilitychange', this._onDocumentVisibilitychange);
  }

  @action
  private _onDocumentVisibilitychange = () => {
    if (document.visibilityState === 'visible') {
      this._loadModels();
      this.loadActiveModels();
    }
  };

  @action
  private _onModelsReloaded = async () => {
    await this._loadModels();
    await this.loadActiveModels();
  };

  @action
  private _onDocumentClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!this._isDropdownOpen || target.closest('.model-selector')) {
      return;
    }
    this._isDropdownOpen = false;
    this._searchFilter = '';
  };

  @action
  private _onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this._searchFilter = event.target.value;
  };

  @action
  private _onCategoryFilterToggle = (category: string) => {
    if (this._categoryFilters.has(category)) {
      this._categoryFilters.delete(category);
    } else {
      this._categoryFilters.add(category);
    }
  };

  @computed
  get searchTerms(){
      const searchStr = this._searchFilter;
      return searchStr.split(/[ ,]+/).filter(x => !!x);
  }

  @action
  private _clearFilters = () => {
    this._searchFilter = '';
    this._categoryFilters.clear();
  };

  private _getFilteredModels = (): Model[] => {
    let filteredList = this._models.filter(m => m.capabilities.includes('completion'));

    // Apply search filter
    const searchStringParts = this.searchTerms;
    if (searchStringParts.length) {
      filteredList = filteredList.filter((power): boolean => {
        let result = true;

        for(const term of searchStringParts){
          let anyMatch = false;

          if (defaulComparator(power.name, term, 0) !== -1){
              anyMatch = true;
          }

          if (defaulComparator(power.server, term, 0) !== -1){
              anyMatch = true;
          }
          if (!anyMatch){
              result = false;
          }
        }

        return result;
      });
    }

    // Apply category filters
    if (this._categoryFilters.size > 0) {
      filteredList = filteredList.filter(model => {
        return Array.from(this._categoryFilters).every(category => 
          model.capabilities.includes(category)
        );
      });
    }

    return filteredList;
  };

  render() {
    if (this._loader.isPending && !this._models.length) {
      return <div>Loading models...</div>;
    }

    if (this._models.length === 0) {
      return <div>No models available</div>;
    }

    const { model } = this.props;
    const filteredModels = this._getFilteredModels();
    const hasActiveFilters = this._searchFilter.trim() || this._categoryFilters.size > 0;
    const uniqueCapabilities: Set<string> = new Set();
    for(const m of this._models){
      for(const c of m.capabilities){
        uniqueCapabilities.add(c);
      }
    }
    const categoryFilters = ['thinking', 'vision', 'tools'].filter(c => uniqueCapabilities.has(c));

    return (
      <div className="model-selector">
          <div className="model-dropdown" onClick={this._onToggleModelDropdownClick}>
              {this._renderModel(model)}
              <span>▼</span>
          </div>
          <div className={'dropdown-menu' + (this._isDropdownOpen ? ' show' : '')}>
              {/* Search and Filter Controls */}
              <div className="dropdown-filters">
                <div className="search-container">
                  <input
                    ref={this._searchInputRef}
                    type="text"
                    placeholder="Search models..."
                    value={this._searchFilter}
                    onChange={this._onSearchChange}
                    className="search-input"
                    onClick={e => e.stopPropagation()}
                  />
                </div>
                
                {(!!categoryFilters.length || hasActiveFilters) && <div className="category-filters">
                  {categoryFilters.map(category => (
                    <button
                      key={category}
                      className={`filter-btn ${this._categoryFilters.has(category) ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        this._onCategoryFilterToggle(category);
                      }}
                    >
                      {category}
                    </button>
                  ))}
                  
                  {hasActiveFilters && (
                    <button 
                      className="clear-filters-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        this._clearFilters();
                      }}
                    >
                      Clear filters
                    </button>
                  )}
                </div>}

              </div>

              {/* Model List */}
              <div className="dropdown-items">
                {this._loader.isPending && <div className="loading-state">Loading models...</div>}
                {filteredModels.length === 0 && !this._loader.isPending && (
                  <div className="no-results">No models match your filters</div>
                )}
                {filteredModels.map((model, index) => {
                  const isActive = this._activeModels.find(activeModel =>
                    activeModel.server === model?.server && activeModel.name === model?.name
                  );

                  if (!isActive && !model.capabilities.includes('completion')){
                    return null; // Skip models that do not support completion
                  }

                  return (
                    <div className="dropdown-item" key={index} onClick={() => this._onSelectModel(model)}>
                      {this._renderModel(model)}
                    </div>
                  );
                })}
              </div>
          </div>
      </div>
    );
  }

  private getModelName(model: Model): string {
    let modelName = model?.name || '';
    //if modelName ends with :latest, replace with actual parameters size

    // round parameters size to number, no decimal, so ...24.3B => ...24B
    const parameter_size = model?.parameter_size ?
      model.parameter_size.replace(/(\d+)(\.\d+)?B/, (_, p1) => {
        return parseInt(p1, 10).toString() + 'B';
      }) : '';

    if (model && modelName.endsWith(':latest')) {
      modelName = modelName.replace(':latest', `:${parameter_size}`);
    }
    if (model && modelName.endsWith(model.quantization_level)) {
      modelName = modelName.replace(model.quantization_level, `${parameter_size}`);
    }
    modelName = modelName.replace('-GGUF', ''); // Remove -GGUF if present
    modelName = modelName.replace('GGUF', ''); // Remove GGUF if present
    if (modelName && modelName.includes('/')) {
      modelName = modelName.split('/').pop() || modelName; // Get the last part after '/'
    }
    return modelName;
  }

  private _renderText = (text: string | undefined | null) => {
    const { searchTerms } = this;

    const parts = stringSearchService.markMatches(text, searchTerms, defaulComparator);
    return (<>
      {parts.map((p, index) => p.isMatch ? <span className="text-match" key={'t' + index}>{p.text}</span> : p.text)}
    </>);
  };

  private _renderModel(model?: Model | null) {
    const modelName = model ? this.getModelName(model) : '';

    const activeModel = this._activeModels.find(activeModel =>
      activeModel.server === model?.server && activeModel.name === model?.name
    );
    
    return (
      <div className="model-info">
        <div className="model-name">{this._renderText(modelName)}</div>
        <div className="model-capabilities">
          {model?.capabilities.filter(c => c === 'vision' || c === 'tools' || c === 'thinking').map((capability, i) => (
            <span className="capability-tag" key={i}>{capability}</span>
          ))}
          {model?.performanceLevel === 'partial' && <span className="capability-tag">🐌</span>}
        </div>
        {activeModel && <MemoryBar vram={activeModel.size_vram} total={activeModel.size} />}
      </div>
    );
  }

  @action
  private _onToggleModelDropdownClick = () => {
    this._isDropdownOpen = !this._isDropdownOpen;
    if (this._isDropdownOpen && this._searchInputRef) {
      // Focus search input when dropdown opens
      setTimeout(() => this._searchInputRef.current?.focus(), 100);
    }
  };

  private _loadModels = async () => {
    const tags = await apiService.get<Model[]>('/tags/full', { loader: this._loader });

    runInAction(() => {
      this._models = tags;
      this._models.sort((a, b) => a.server.localeCompare(b.server) || this.getModelName(a).localeCompare(this.getModelName(b)));

      const savedModelName = localStorage.getItem('currentModel');
      let isModelSelected = false;
      if (savedModelName) {
        const [server, name] = savedModelName.split('@');
        const currentModel = this._models.find(model => model.server === server && model.name === name);
        if (currentModel) {
          isModelSelected = true;
          if (this.props.model?.server !== currentModel.server || this.props.model?.name !== currentModel.name) {
            this.props.onModelChange(currentModel);
          }
        }
      }

      if (!isModelSelected && this._models.length) {
        this.props.onModelChange(this._models[0]);
      }
    });
  };

  public loadActiveModels = async () => {
    const activeModels = await apiService.get<LoadedModel[]>('/tags/active');
    runInAction(() => {
      this._activeModels = activeModels;
    });
  }

  @action
  private _onSelectModel = (model: Model) => {
    this.props.onModelChange(model);
    localStorage.setItem('currentModel', model.server + '@' + model.name);
    this._isDropdownOpen = false;
    this._searchFilter = '';
  };

  @action
  public trySetModel(modelName: string) {
    const model = this._models.find(m => m.name === modelName);
    if (model) {
      this.props.onModelChange(model);
      localStorage.setItem('currentModel', model.server + '@' + model.name);
      return true;
    }
    return false;
  }

  public setModelWithCapability(capability: string, bestModels: string[]) {
    //search models with the given capability, given priority to the bestModels in order they are passes, first one is the best
    const models = this._models.filter(m => m.capabilities.includes(capability));
    
    //order models by bestModels priority
    models.sort((a, b) => {
      const aIndex = bestModels.indexOf(a.name);
      const bIndex = bestModels.indexOf(b.name);
      if (aIndex === -1 && bIndex === -1) return 0; // neither model is in the bestModels list
      if (aIndex === -1) return 1; // a is not in the list, b is better
      if (bIndex === -1) return -1; // b is not in the list, a is better
      return aIndex - bIndex; // both are in the list, order by index
    });

    const bestModel = models[0];
    if (bestModel) {
      this.props.onModelChange(bestModel);
      return true;
    }
    return false;
  }
}

export default ModelSelector;